// OrlDeform — the EXACT MetaHuman rig in place of a branch's distilled
// rig2mesh ONNX (controls[1,D] -> vertices[1,V,3]).
//
// Drop-in for OGSRig2Mesh: same pose()/runCpu()/download() surface, so
// OGSBranch's calibrate() and CPU pose path consume it unchanged.
//
// WHY THIS IS EQUIVALENT (measured, isaac-ogs head branch vs rig2mesh_head.onnx):
//   - the branch mesh IS MetaHuman head_lod0 — 24,049 verts / 48,004 faces,
//     exactly what the ORL bundle bakes, and the bundle's rig_names.json is the
//     same 174-wide control space
//   - compared as DISPLACEMENT FROM NEUTRAL — the domain that matters, because
//     OGSBranch.calibrate() folds the constant per-vertex difference into `corr`
//     — the two agree to 0.26-0.70 mm median against 7-34 mm of actual motion
//   - the absolute neutral shape differs by 3.96 mm median and the ONNX is
//     origin-centred cm vs ORL's rig world space; `corr` absorbs both
//   - 163/174 of the bundle's controls map to DNA GUI controls. The 11 that
//     don't (eye aim, lipsPressD, viewport switches) each move the head mesh
//     <= 0.6 mm through the ONNX model — under its own ~4 mm noise — except
//     CTRL_C_eye.translateY at 5.8 mm (eyelid follow). Reference: jaw open is
//     32.6 mm. So the gap is real but small and localized to the eyes.
//
// GPU-RESIDENT. The wasm rig eval is CPU (it is tiny — 174 control writes plus
// an 870-joint hierarchy walk), but the DEFORM runs in WGSL on the lifter's own
// device and its output goes straight into setPoseGpu. Per frame that uploads
// ~45 KB of skin rows + blendshape weights instead of 288 KB of vertices, does
// no readback, and needs no fence: one device means one queue, and this pass is
// submitted before the lifter's copy. Falls back to the CPU deform only when
// there is no lift device (CPU lifter) or ?gpudeform=0.

import { createOrlRig } from "../../../lib/orl/orlRig.js";
import { buildBlendshapeCsr } from "../../../lib/orl/csr.js";
import type { DirtySource, FrameInputs, Node, NodeContext, NodeTensor, PortSpec } from "../types";

export interface OrlDeformOptions {
  id: string;
  /** Mounted `orl_*` character assets (the pack VFS) — the bundle rides with the
   *  character, so there is no identity to name. */
  getBytes: (name: string) => Uint8Array | undefined;
  /** The bundle's rig_names.json — the order the branch supplies rig values in. */
  controlNames: string[];
  /** Branch mesh vertex count; a mismatch throws rather than posing garbage. */
  expectVerts?: number;
  rigInputPort?: string;
  outputPort?: string;
  /** ?gpudeform — false keeps the deform on the CPU (the documented
   *  kill-switch for "rig2mesh verts stay gpu-resident"). */
  gpuDeform?: boolean;
}

type OrlRig = Awaited<ReturnType<typeof createOrlRig>>;

export class OrlDeform implements Node {
  readonly id: string;
  readonly type = "orlDeform";
  readonly inputs: readonly PortSpec[];
  readonly outputs: readonly PortSpec[];
  readonly bindings = [];

  private readonly rigPort: string;
  private readonly outPort: string;
  private readonly getBytes: (name: string) => Uint8Array | undefined;
  private readonly controlNames: string[];
  private readonly expectVerts: number;
  private rig: OrlRig | null = null;
  private dims: number[] = [];
  private readonly wantGpuDeform: boolean;
  /** Set by initGpu when a lift device exists; null keeps the CPU deform. */
  private gpu: { deform(j: Float32Array, b: Float32Array): { gpuBuffer: GPUBuffer; byteLength: number };
                 readback(): Promise<Float32Array>; destroy(): void } | null = null;

  constructor(opts: OrlDeformOptions) {
    this.id = opts.id;
    this.getBytes = opts.getBytes;
    this.controlNames = opts.controlNames;
    this.expectVerts = opts.expectVerts ?? 0;
    this.wantGpuDeform = opts.gpuDeform !== false;
    this.rigPort = opts.rigInputPort ?? "rig";
    this.outPort = opts.outputPort ?? "verts";
    this.inputs = [{ name: this.rigPort }];
    this.outputs = [{ name: this.outPort }];
  }

  async init(_ctx: NodeContext): Promise<void> {
    this.rig = await createOrlRig({
      getBytes: this.getBytes,
      controlNames: this.controlNames,
      expectVerts: this.expectVerts,
      // The bundle's control list is MetaHuman-named, so a healthy map resolves
      // nearly all of it; anything near-empty means the wrong DNA for this
      // bundle and must fail loudly rather than render a frozen face.
      minMatched: Math.floor(this.controlNames.length * 0.8),
      log: (m: string) => console.log(`[ogs] ${this.id}: ${m}`),
    });
    this.dims = [1, this.rig.V, 3];
  }

  /** Phase 2, after the lift device exists. Builds the WGSL deform on THAT
   *  device — it must be the same one GpuLifter got, or the lifter's
   *  copyBufferToBuffer from our output is a cross-device validation error.
   *  A null device (CPU lifter) or ?gpudeform=0 leaves the CPU deform in place. */
  async initGpu(device: GPUDevice | null): Promise<void> {
    if (!this.rig || !device || !this.wantGpuDeform) {
      if (this.rig && !this.wantGpuDeform) console.log(`[ogs] ${this.id}: ?gpudeform=0 — deform stays on the CPU`);
      return;
    }
    try {
      const { createOrlGpuDeform } = await import("../../../lib/orl/OrlGpuDeform.js");
      const d = this.rig.deformer;
      const csr = buildBlendshapeCsr(d.V, d.buffers.bsIndex, d.buffers.bsDelta, d.bsTargets);
      this.gpu = createOrlGpuDeform(device, d, csr, this.rig.manifest.numBlendShapeChannels);
      console.log(`[ogs] ${this.id}: GPU deform ready (${csr.entries} blendshape entries) — verts stay on-device`);
    } catch (e) {
      // Loud, but not fatal: the CPU deform still produces correct vertices.
      console.warn(`[ogs] ${this.id}: GPU deform unavailable, falling back to the CPU deform`, e);
      this.gpu = null;
    }
  }

  /** Rig -> posed verts, in CENTIMETRES — the rig's own world space, which
   *  OGSBranch.calibrate() maps onto the checkpoint's.
   *
   *  GPU path: returns a gpu-buffer tensor OGSBranch hands to setPoseGpu, so the
   *  vertices never touch the CPU. CPU path: the deformer's reused scratch,
   *  which OGSBranch copies out of immediately (same contract as ORT's reused
   *  output buffer). */
  pose(rig: Float32Array): NodeTensor {
    if (!this.rig) throw new Error(`OrlDeform "${this.id}": init not run`);
    if (this.gpu) {
      const { jointOut, bsOut } = this.rig.evaluateRig(rig);
      const out = this.gpu.deform(jointOut, bsOut);
      // Same shape liftInputToNodeTensor() produces for a non-ORT gpu buffer.
      return { loc: "gpu-buffer", tensor: { gpuBuffer: out.gpuBuffer }, dims: this.dims } as unknown as NodeTensor;
    }
    return { loc: "cpu", data: this.rig.evaluate(rig), dims: this.dims } as NodeTensor;
  }

  /** The DNA's RAW control layer, for the Rig Preview panel's readout.
   *
   *  `guiToRaw` is the correspondence between the layer the bundle's controls
   *  are written in and the one RigLogic evaluates — a UI-cadence call, never on
   *  the per-frame path (see orlRig.js). Both are null/empty before init or on a
   *  riglogic.wasm predating the raw exports, so a consumer checks rather than
   *  assuming the layer is reachable. */
  get rawLayer(): {
    names: string[];
    controlSpace: string;
    guiToRaw: (v: Float32Array) => Float32Array | null;
    rawToControls: (v: Float32Array) => Float32Array | null;
    drivable: () => boolean[];
  } | null {
    if (!this.rig || !this.rig.rawNames?.length) return null;
    return {
      names: this.rig.rawNames,
      controlSpace: this.rig.controlSpace,
      guiToRaw: (v: Float32Array) => this.rig!.guiToRaw(v),
      // The way BACK. An edit here has to return to the bundle's own control
      // vector, because that is what the decoders take — see rawToControls.
      rawToControls: (v: Float32Array) => this.rig!.rawToControls(v),
      // Lazy: builds the inverse table on first call, which a readout-only
      // session should not pay for.
      drivable: () => this.rig!.drivableRaw(),
    };
  }

  /** DEBUG: the GPU deform's output, read back once. Null on the CPU path.
   *  Used by the correctness gate — never on the per-frame path. */
  async debugReadback(): Promise<Float32Array | null> {
    return this.gpu ? this.gpu.readback() : null;
  }

  async evaluate(
    _frame: FrameInputs,
    inbound: Readonly<Record<string, NodeTensor>>,
  ): Promise<Record<string, NodeTensor>> {
    const rig = inbound[this.rigPort];
    if (!rig) throw new Error(`OrlDeform "${this.id}": missing input "${this.rigPort}"`);
    if (rig.loc !== "cpu") throw new Error(`OrlDeform "${this.id}": rig must be on the CPU`);
    return { [this.outPort]: this.pose(rig.data as Float32Array) };
  }

  /** Calibration path. Deliberately uses the CPU deform even when the GPU one is
   *  live: `corr = neutral - runCpu(baseRig)` is added to every GPU-produced
   *  frame, so the two must agree — which the C3 gate asserts to 1e-3 cm. */
  async runCpu(rig: Float32Array): Promise<Float32Array> {
    if (!this.rig) throw new Error(`OrlDeform "${this.id}": init not run`);
    return Float32Array.from(this.rig.evaluate(rig));
  }

  async download(t: NodeTensor): Promise<Float32Array> {
    if (t.loc === "cpu") return t.data as Float32Array;
    const back = await this.debugReadback();
    if (!back) throw new Error(`OrlDeform "${this.id}": no GPU deform to download from`);
    return back;
  }

  /** No ORT session — the gpu-buffer demote path and profile dump don't apply. */
  get activeSession(): null {
    return null;
  }

  dirtyOn(): readonly DirtySource[] {
    return ["rig"];
  }

  dispose(): void {
    try { this.gpu?.destroy(); } catch { /* best-effort */ }
    this.gpu = null;
    // Paired with createOrlRig(): the rig is SHARED with any sibling branch
    // driving the same pack, so dropping the reference is not enough — the last
    // holder to release is what frees the wasm heap.
    try { this.rig?.release(); } catch { /* best-effort */ }
    this.rig = null;
  }
}
